import React, { useState, useEffect, useRef } from 'react';

import { useResizeObserver } from 'usehooks-ts';

import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Table, Modal, Tag, Select, DatePicker, notification, Flex, Form, Switch, Tooltip, InputNumber } from 'antd';
import { ColumnsType } from 'antd/es/table';
import {
    LoadingOutlined,
    CloseOutlined,
    ReloadOutlined,
    MinusOutlined,
    PlusOutlined,
    SwapOutlined,
    QuestionCircleOutlined,
    CheckOutlined,
    ClockCircleOutlined,
    EditOutlined,
} from '@ant-design/icons';

import TransferAccountsModal from '@components/balance-details/transfer-accounts-modal';
import CancelTransactionModal from '@components/transactions/cancel-transaction-modal';
import IBaseComponentProps from '@components/base-component-props';

import Toolbar from '@controls/toolbar/toolbar';
import Filter from '@controls/filter/filter';
import Account from '@controls/account';

import { exception, securityRestriction, warning } from '@extensions/notification';
import { delayAction, toFinanceString, getEnumList, getDateTimeLocal } from '@extensions/utils';
import { userLoaded, setFilter } from '@store/actions';
import { useAppSelector, useAppDispatch } from '@store/hooks';

import { ITransaction } from '@entities/transaction';
import { ITransactionFilter } from '@entities/transaction-filter';
import { IUserSession } from '@entities/user-session';
import { IUserAccount } from '@entities/user-account';
import { IUser } from '@entities/user';

import { Currency, enumSign as currencySign } from '@enums/currency';
import { TransactionType, enumLabel as TransactionTypeLabel } from '@enums/transaction-types';
import { IEnumItem } from '@enums/enum-item';
import { OperationType, enumLabel as OperationTypeLabel } from '@enums/operation-type';
import { Permission, hasPermission } from '@enums/permission';
import { TransactionStatus, enumLabel as TransactionStatusLabel } from '@enums/transaction-status';
import { UserAccountType } from '@enums/user-account-type';
import { UserBalanceType } from '@enums/user-balance-type';

dayjs.extend(utc);

export interface ICancelParams {
    reason: string;
}

interface ITransactionsProps extends IBaseComponentProps {
    userId: string;
    hideInitiator?: boolean;
    isViewMode: boolean;
    currency: Currency;
    useTagRequired: boolean;
    tag: string | undefined;
    userBalanceType?: UserBalanceType;
    onRefresh?: () => void;
}

const Transactions = (props: ITransactionsProps) => {
    const { userId, useTagRequired, tag, isViewMode, hideInitiator, currency, userBalanceType, onRefresh, serverFetch } = props;

    const filterContext: string = 'Transactions' + (userBalanceType == UserBalanceType.White ? 'White' : '');

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const filterRef = useRef<HTMLDivElement>(null);
    const { width: filterWidth = 0, height: filterHeight = 0 } = useResizeObserver({
        ref: filterRef,
        box: 'border-box',
    });

    const initFilter: ITransactionFilter = {
        userId: userId,
        useTagRequired: useTagRequired,
        //userBalanceType: userBalanceType,
        statuses: [TransactionStatus.Pending, TransactionStatus.Authorised],
        types: [TransactionType.Debiting, TransactionType.Depositing, TransactionType.Transfer],
    };

    const userSession = useAppSelector<IUserSession>((s) => s.userSession);
    const filter = useAppSelector<ITransactionFilter>((s) => s.filters[filterContext]);

    const { RangePicker } = DatePicker;

    const [modal, modalContextHolder] = Modal.useModal();
    const [api, contextHolder] = notification.useNotification();
    const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
    const [currentTransaction, setCurrentTransaction] = useState<ITransaction>();
    const d = useAppDispatch();

    const [transactions, setTransactions] = useState<Array<ITransaction>>([]);
    const [totalDepositing, setTotalDepositing] = useState<number>(0);
    const [totalDebiting, setTotalDebiting] = useState<number>(0);
    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const [showFilter, setShowFilter] = useState<boolean>(true);

    const [transactionStatuses] = useState<Array<IEnumItem>>(getEnumList(TransactionStatus, TransactionStatusLabel));
    const [transactionTypes] = useState<Array<IEnumItem>>(getEnumList(TransactionType, TransactionTypeLabel));
    const [operationTypes] = useState<Array<IEnumItem>>(getEnumList(OperationType, OperationTypeLabel));

    const [user, setUser] = useState<IUser>();
    const [userAccounts, setUserAccounts] = useState<Array<IUserAccount>>([]);

    const [openCancelModal, setOpenCancelModal] = useState<boolean>(false);
    const [openTransferModal, setOpenTransferModal] = useState<boolean>(false);

    const [useTag, setUseTag] = useState<boolean>(false);

    useEffect(() => {
        let cleanup = false;

        if (!refreshRequired) return;

        setTransactions([]);
        setSelectedIds([]);
        setCurrentTransaction(undefined);

        const fetchData = async () => {
            setLoading(true);

            const promises = [
                await serverFetch('transactions', { method: 'GET', queryParams: filter })
                    .then((data: Array<ITransaction>) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения платежных операций', ex, () => d(userLoaded(undefined)));
                    }),
                await serverFetch(`users/${userId}`, { method: 'GET' })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения пользователя', ex, () => d(userLoaded(undefined)));
                    }),
                await serverFetch(`users/${userId}/accounts`, { method: 'GET' })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения счетов пользователя', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                const entries: Array<ITransaction> = result[0][0];
                setTransactions(entries);

                const depositings = entries.filter((d) => d.currency == Currency.Usd && d.type == TransactionType.Transfer);

                let tmpTotalDepositing = 0;

                depositings.map((d) => {
                    tmpTotalDepositing += d.amount;
                });
                setTotalDepositing(tmpTotalDepositing);

                const debitings = entries.filter(
                    (d) =>
                        d.currency == Currency.Usd && d.type == TransactionType.Debiting && d.userAccountType == UserAccountType.Transport,
                );

                setUser(result[0][1]);
                setUserAccounts(result[0][2]);

                let tmpTotalDebiting = 0;
                debitings.map((d) => {
                    tmpTotalDebiting += d.amount;
                });
                setTotalDebiting(tmpTotalDebiting);

                setLoading(false);
                setRefreshRequired(false);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, [refreshRequired]);

    useEffect(() => {
        if (!filter || filter.userId !== userId) {
            d(setFilter(initFilter, filterContext));
            return;
        }

        delayAction(() => setRefreshRequired(true));
    }, [filter]);

    const renderFilter = () => {
        return (
            <div ref={filterRef}>
                <Filter
                    display={showFilter}
                    items={[
                        <InputNumber
                            key='number'
                            placeholder='#'
                            value={filter?.number}
                            style={{ width: 150 }}
                            onChange={(value) => {
                                d(setFilter({ ...filter, number: value }, filterContext));
                            }}
                        />,
                        <Select
                            key='transactionType'
                            placeholder='Тип операции'
                            value={filter?.types}
                            size='middle'
                            mode='multiple'
                            maxTagCount='responsive'
                            allowClear
                            style={{ width: 270 }}
                            onChange={(value: any) => d(setFilter({ ...filter, types: value }, filterContext))}
                            options={transactionTypes.map((t) => {
                                return { value: t.value, label: t.label };
                            })}
                        />,
                        <Select
                            key='transactionStatus'
                            placeholder='Статус операции'
                            value={filter?.statuses}
                            size='middle'
                            mode='multiple'
                            maxTagCount='responsive'
                            allowClear
                            style={{ width: 270 }}
                            onChange={(value: any) => d(setFilter({ ...filter, statuses: value }, filterContext))}
                            options={transactionStatuses.map((t) => {
                                return { value: t.value, label: t.label };
                            })}
                        />,
                        <Select
                            key='operationTypes'
                            placeholder='Тип платежа'
                            value={filter?.operationTypes}
                            size='middle'
                            mode='multiple'
                            maxTagCount='responsive'
                            allowClear
                            style={{ width: 160 }}
                            onChange={(value: any) => d(setFilter({ ...filter, operationTypes: value }, filterContext))}
                            options={operationTypes.map((t) => {
                                return { value: t.value, label: t.label };
                            })}
                        />,
                        <RangePicker
                            style={{ width: 230 }}
                            allowEmpty={[true, true]}
                            key='date'
                            format='DD.MM.YYYY'
                            placeholder={['Операция с', 'до']}
                            cellRender={(current) => {
                                return <div className='ant-picker-cell-inner'>{(current as Dayjs).date()}</div>;
                            }}
                            value={[
                                filter?.createdFrom ? dayjs(filter?.createdFrom) : null,
                                filter?.createdTo ? dayjs(filter?.createdTo) : null,
                            ]}
                            onChange={(value) => {
                                if (!value) {
                                    d(
                                        setFilter(
                                            {
                                                ...filter,
                                                createdFrom: undefined,
                                                createdTo: undefined,
                                            },
                                            filterContext,
                                        ),
                                    );

                                    return;
                                }

                                d(
                                    setFilter(
                                        {
                                            ...filter,
                                            createdFrom:
                                                value && value[0]
                                                    ? dayjs(value[0]).utc(true).startOf('day').toString()
                                                    : undefined,
                                            createdTo:
                                                value && value[1]
                                                    ? dayjs(value[1]).utc(true).endOf('day').toString()
                                                    : undefined,
                                        },
                                        filterContext,
                                    ),
                                );
                            }}
                        />,
                        <Form.Item key='tag' label={tag} style={{ margin: 0, display: useTagRequired ? 'block' : 'none' }}>
                            <Switch
                                checked={useTag}
                                onChange={(value: boolean) => {
                                    setUseTag(value);

                                    if (value) {
                                        d(setFilter({ ...filter, tag: tag }, filterContext));
                                    } else {
                                        d(setFilter({ ...filter, tag: undefined }, filterContext));
                                    }
                                }}
                            />
                        </Form.Item>,
                    ]}
                    onClear={() => {
                        d(setFilter(initFilter, filterContext));
                        setUseTag(false);
                    }}
                />
            </div>
        );
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    {
                        label: 'Изменить',
                        key: 'edit',
                        disabled:
                            !currentTransaction || currentTransaction.type != TransactionType.Transfer || !currentTransaction.exchangeRate,
                        hidden: isViewMode,
                        icon: <EditOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageTransactions)) {
                                securityRestriction(api, [Permission.ManageTransactions]);
                                return;
                            }

                            modal.confirm({
                                title: `Изменить выбранную платежную операцию?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'ОК',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    setOpenTransferModal(true);
                                },
                            });
                        },
                    },
                    {
                        label: 'Отменить',
                        key: 'cancel',
                        disabled: !currentTransaction,
                        hidden: isViewMode,
                        icon: <CloseOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.CancelTransaction)) {
                                securityRestriction(api, [Permission.CancelTransaction]);
                                return;
                            }

                            if (currentTransaction?.parentId != undefined) {
                                const parent = transactions.find((t) => t.id == currentTransaction.parentId);
                                let warningMessage: string = `Для отмены выбранной платежной операции отмените основную платежную операцию #${parent?.number}`;

                                warning(api, warningMessage);
                                return;
                            }

                            modal.confirm({
                                title: `Отменить выбранную  платежную операцию?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'ОК',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    setOpenCancelModal(true);
                                },
                            });
                        },
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const onSelectChange = (selectedRowKeys: React.Key[]) => {
        setSelectedIds(selectedRowKeys);

        if (selectedRowKeys.length == 1) {
            const entity = transactions.find((o) => o.id == selectedRowKeys[0]);
            setCurrentTransaction(entity);
        } else {
            setCurrentTransaction(undefined);
        }
    };

    const renderTable = () => {
        const columns: ColumnsType<ITransaction> = [
            {
                title: '#',
                width: 80,
                align: 'center',
                dataIndex: 'number',
            },
            {
                title: 'Операция',
                width: 190,
                render: (_, record) => {
                    return Operation(
                        record.amount,
                        record.currency,
                        record.userAccountType,
                        record.userBalanceType,
                        record.type,
                        record.status,
                        record.operationType,
                        record.debtName,
                    );
                },
            },
            {
                title: 'ИНН',
                align: 'center',
                width: 120,
                render: (_, record) => {
                    return record.inn;
                },
            },
            {
                title: 'Получатель',
                align: 'center',
                width: 250,
                render: (_, record) => {
                    return record.companyName;
                },
            },
            {
                title: 'Комиссия %',
                align: 'center',
                width: 110,
                render: (_, record) => {
                    if (!record.commission) return undefined;

                    return record.commission && record.commission.toFixed(2);
                },
            },
            {
                title: 'Дата',
                align: 'center',
                width: 140,
                render: (_, record) => {
                    return getDateTimeLocal(record.createdOn);
                },
            },
            {
                title: 'Инициатор',
                dataIndex: 'createdByName',
                width: 250,
                hidden: hideInitiator,
            },
            {
                title: 'Детали',
                width: 250,

                render: (_, record) => {
                    return Comment(record.type, record.billFullNumber, record.billId, record.comment, record.debtName);
                },
            },
            {},
        ];

        const tableLoading = {
            spinning: loading,
            indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
        };

        return (
            <Table
                rowKey='id'
                size='small'
                columns={columns}
                dataSource={transactions}
                loading={tableLoading}
                pagination={false}
                rowSelection={
                    isViewMode
                        ? undefined
                        : {
                              selectedRowKeys: selectedIds,
                              onChange: onSelectChange,
                              columnWidth: 32,
                              type: 'radio',
                              getCheckboxProps: (record) => ({
                                  style: {
                                      display:
                                          record.type == TransactionType.Debiting || record.type == TransactionType.DebtWriteOff
                                              ? 'none'
                                              : '',
                                  },
                              }),
                          }
                }
                onRow={(record: ITransaction) => {
                    return {
                        onClick: (event) => {
                            if (!record.id) return;

                            onSelectChange([record.id]);
                        },
                    };
                }}
                footer={() => (
                    <span
                        style={{
                            fontWeight: 700,
                            fontSize: 16,
                        }}
                    >
                        <span style={{ marginLeft: 5 }}>
                            Всего внесено
                            <Tag color='var(--main-green)' style={{ marginLeft: 5, fontSize: 18 }}>
                                +{toFinanceString(totalDepositing, 2)} {currencySign(currency)}
                            </Tag>
                        </span>

                        <span style={{ marginLeft: 5 }}>
                            Всего потрачено
                            <Tag color='var(--main-red)' style={{ marginLeft: 5, fontSize: 18 }}>
                                -{toFinanceString(totalDebiting, 2)} {currencySign(currency)}
                            </Tag>
                        </span>
                    </span>
                )}
                virtual={true}
                scroll={{ y: height ?? 0 + (showFilter ? 0 : 58) }}
            />
        );
    };

    return (
        <div ref={containerRef} style={{ height: `calc(100vh - 237px - ${filterHeight}px)` }}>
            {renderToolbar()}
            {renderFilter()}
            {renderTable()}

            {currentTransaction && openCancelModal && (
                <CancelTransactionModal
                    userId={userId}
                    transaction={currentTransaction}
                    api={api}
                    onSave={() => {
                        setOpenCancelModal(false);
                        setRefreshRequired(true);
                        if (onRefresh) onRefresh();
                    }}
                    onCancel={() => setOpenCancelModal(false)}
                    serverFetch={serverFetch}
                />
            )}

            {currentTransaction && openTransferModal && (
                <TransferAccountsModal
                    userId={user?.id || ''}
                    userLogin={user?.login || ''}
                    accounts={userAccounts || []}
                    onCancel={() => setOpenTransferModal(false)}
                    onSave={() => {
                        setRefreshRequired(true);
                        setOpenTransferModal(false);
                    }}
                    transaction={currentTransaction}
                    api={api}
                    serverFetch={serverFetch}
                />
            )}

            {contextHolder}
            {modalContextHolder}
        </div>
    );
};

export default Transactions;

export const TransactionTypeBackgroundColor = (type?: TransactionType) => {
    switch (type) {
        case TransactionType.Debiting:
        case TransactionType.DebtWriteOff: {
            return '#fbe7eb';
        }
        case TransactionType.Refund: {
            return '#f2e5f2';
        }
        case TransactionType.Depositing: {
            return '#e8edeb';
        }
        case TransactionType.Transfer: {
            return '#e5e5ff';
        }
        default:
            return '';
    }
};

export const TransactionTypeColor = (type?: TransactionType) => {
    switch (type) {
        case TransactionType.Debiting:
        case TransactionType.DebtWriteOff:
        case TransactionType.Refund:
            return 'var(--main-red)';
        case TransactionType.Depositing:
            return 'var(--main-green)';
        case TransactionType.Transfer:
            return 'blue';
        default:
            return '';
    }
};

export const TransactionStatusColor = (type?: TransactionStatus) => {
    switch (type) {
        case TransactionStatus.Canceled:
            return 'var(--main-red)';
        case TransactionStatus.Authorised:
            return 'var(--main-green)';
        case TransactionStatus.Pending:
            return '#808080';
        default:
            return '';
    }
};

export const Operation = (
    amount?: number,
    currency?: Currency,
    userAccountType?: UserAccountType,
    userBalanceType?: UserBalanceType,
    transactionType?: TransactionType,
    transactionStatus?: TransactionStatus,
    operationType?: OperationType,
    debtName?: string,
) => {
    if (!userAccountType || !currency) return <></>;

    let operationIcon = <></>;
    let operation = <></>;
    let statusIcon = <></>;

    const currencyIcon = <Account type={userAccountType} currency={currency} userBalanceType={userBalanceType} />;

    switch (operationType) {
        case OperationType.Cash: {
            operation = (
                <Tag color='green' style={{ color: '#000000' }}>
                    Наличный
                </Tag>
            );
            break;
        }
        case OperationType.NonCash: {
            operation = (
                <Tag color='var(--primary-color)' style={{ color: '#000000' }}>
                    Безналичный
                </Tag>
            );
            break;
        }
    }

    if (transactionType == TransactionType.DebtWriteOff) {
        operation = (
            <Tag color='var(--main-red)' style={{ color: '#FFFFFF' }}>
                {debtName}
            </Tag>
        );
    }

    const transactionTypeColor = TransactionTypeColor(transactionType);

    switch (transactionType) {
        case TransactionType.DebtWriteOff:
        case TransactionType.Debiting:
        case TransactionType.Refund: {
            operationIcon = <MinusOutlined style={{ color: transactionTypeColor }} />;
            break;
        }
        case TransactionType.Depositing: {
            operationIcon = <PlusOutlined style={{ color: transactionTypeColor }} />;
            break;
        }
        case TransactionType.Transfer: {
            operationIcon = <SwapOutlined style={{ color: transactionTypeColor }} />;
            break;
        }
    }

    const transactionStatusColor = TransactionStatusColor(transactionStatus);

    switch (transactionStatus) {
        case TransactionStatus.Pending: {
            statusIcon = (
                <Tooltip title={TransactionStatusLabel(transactionStatus)} color={transactionStatusColor}>
                    <ClockCircleOutlined style={{ color: transactionStatusColor }} />
                </Tooltip>
            );
            break;
        }
        case TransactionStatus.Authorised: {
            statusIcon = (
                <Tooltip title={TransactionStatusLabel(transactionStatus)} color={transactionStatusColor}>
                    <CheckOutlined style={{ color: transactionStatusColor }} />
                </Tooltip>
            );
            break;
        }
        case TransactionStatus.Canceled: {
            statusIcon = (
                <Tooltip title={TransactionStatusLabel(transactionStatus)} color={transactionStatusColor}>
                    <CloseOutlined style={{ color: transactionStatusColor }} />
                </Tooltip>
            );
        }
    }

    return (
        <Flex gap='middle' align='center'>
            <Flex gap='small' align='center'>
                <div>{statusIcon}</div>
                <div>{currencyIcon}</div>
            </Flex>

            <div>
                {operationIcon}
                <span style={{ color: transactionTypeColor, fontWeight: 600, marginLeft: 5 }}>{toFinanceString(amount, 2)}</span>
                <div>{operation}</div>
            </div>
        </Flex>
    );
};

export const Comment = (
    type?: TransactionType,
    billFullNumber?: string,
    billId?: string,
    comment?: string,
    debtName?: string,
    tableRef?: any,
) => {
    return (
        <>
            {type == TransactionType.Depositing && !comment ? (
                <div>Внесение</div>
            ) : type == TransactionType.Debiting ? (
                <div>
                    <span>Списание по счету </span>
                    <a
                        style={{ fontWeight: 600 }}
                        onClick={() => {
                            if (tableRef) tableRef.current?.scrollTo({ key: billId });
                        }}
                    >
                        #{billFullNumber}
                    </a>
                </div>
            ) : type == TransactionType.DebtWriteOff ? (
                <div>
                    Списание задолженности <span style={{ fontWeight: 600 }}>"{debtName}" </span>
                    {billFullNumber ? (
                        <span>
                            <span>по счету </span>
                            <a
                                style={{ fontWeight: 600 }}
                                onClick={() => {
                                    if (tableRef) tableRef.current?.scrollTo({ key: billId });
                                }}
                            >
                                #{billFullNumber}
                            </a>
                        </span>
                    ) : undefined}
                </div>
            ) : (
                <></>
            )}

            {comment && <div style={{ whiteSpace: 'break-spaces' }}>{comment}</div>}
        </>
    );
};
